/* eslint-disable custom/no-unsafe-deep-access */
import { generatePath } from 'react-router';
import uuid from 'uuid/v4';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import each from 'lodash/each';
import pick from 'lodash/pick';
import includes from 'lodash/includes';
import omit from 'lodash/omit';
import isNaN from 'lodash/isNaN';
import parseInt from 'lodash/parseInt';
import findIndex from 'lodash/findIndex';
import extend from 'lodash/extend';
import sortBy from 'lodash/sortBy';
import store from '@Store';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { citations } from '@Constants';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';

import AppStorage from '@Services/AppStorage';
import ExportReopenService from '@Services/ExportReopenService';
import Catalyst from '@Services/Catalyst';
import ExportService from '@Services/ExportService';
import ExportRequestManager from '@Services/ExportRequestManager';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';

import { eachHierarchy } from '@Hooks/LibraryInitialization/MoreUnderscore';
import { updateDialog, closeDialog } from '@Containers/Dialogs/DialogsActions';
import { normalizedCheckedItems as normalizedCheckedItemsSelector } from '@Pages/Results/selectors';
import { similarityMolSelector } from '@Containers/SortingProperties/SortingPropertiesSelectors';

import { EXPORT_TYPES, EXPORT_TEXTS } from '@Services/ExportService/ExportConstants';
import {
  entries as entriesSelector,
  errors as errorsSelector,
  data as dataSelector,
  formData as formDataSelector,
  isSelectionExportActive as isSelectionExportActiveSelector,
  formatsBackup as formatsBackupSelector,
  exportType as exportTypeSelector,
  selectedDatapoints as selectedDatapointsSelector,
  isResultsExport as isResultsExportSelector,
  isChemQuoteExport as isChemQuoteExportSelector,
  isBusy as isBusySelector,
} from './ExportModalSelectors';

import {
  typeOfExportModal,
  exportModalSetDataReducerType,
  exportModalSetIsLoadingReducerType,
  exportModalSetCurrentViewReducerType,
  exportModalSetErrorsReducerType,
  EXPORT_VIEWS,
  exportModalSetEntriesReducerType,
  exportModalSetFormDataReducerType,
  exportModalSetSelectedDatapointsReducerType,
  exportModalSetFormatsBackupReducerType,
  exportModalSetExportTypeReducerType,
  EXPORT_ERRORS,
  exportModalResetStateReducerType,
} from './ExportModalConstants';

let exportRequest = null;
let datapoints;
let locationChangeSubscription;

export const openExportModal = (args) => (dispatch) => {
  dispatch(
    updateDialog({
      id: typeOfExportModal,
      dialogType: typeOfExportModal,
      movable: false,
      backdrop: true,
      ...args,
    })
  );
};

export const closeExportModal = () => (dispatch) => {
  dispatch(
    closeDialog({
      id: typeOfExportModal,
    })
  );

  if (locationChangeSubscription) {
    locationChangeSubscription();
  }
};

export const dispatchOpenExportModal = (...args) => {
  store.dispatch(openExportModal(...args));
};

export const setIsLoading = (isLoading) => ({
  type: exportModalSetIsLoadingReducerType,
  payload: { isLoading },
});

export const setCurrentView = (currentView) => ({
  type: exportModalSetCurrentViewReducerType,
  payload: { currentView },
});

export const setEntries = (entries) => ({
  type: exportModalSetEntriesReducerType,
  payload: { entries },
});

export const setData = (data) => ({
  type: exportModalSetDataReducerType,
  payload: { data: { ...data } },
});

export const setFormData = (formData) => ({
  type: exportModalSetFormDataReducerType,
  payload: { formData: { ...formData } },
});

export const setSelectedDatapoints = (selectedDatapoints) => ({
  type: exportModalSetSelectedDatapointsReducerType,
  payload: { selectedDatapoints },
});

export const setFormatsBackup = (formatsBackup) => ({
  type: exportModalSetFormatsBackupReducerType,
  payload: { formatsBackup },
});

export const setExportType = (exportType) => ({
  type: exportModalSetExportTypeReducerType,
  payload: { exportType },
});

export const setErrors = (type, value) => ({
  type: exportModalSetErrorsReducerType,
  payload: { type, value },
});

export const resetState = () => ({
  type: exportModalResetStateReducerType,
});

export const getWarningRangeVisible = (type) => (dispatch, getState) => {
  const data = dataSelector(getState());

  // data.ranges is not ready, return false
  if (!data.ranges) {
    return false;
  }

  const formData = formDataSelector(getState());
  const entries = entriesSelector(getState());

  const {
    range: { selected, to, from },
  } = formData;

  const { exportSizeLimit, ranges } = data;

  // Run the checks
  if (type === 'high') {
    return (
      (selected === 0 && ranges[0]?.value > exportSizeLimit) ||
      (selected === 2 && to - from >= exportSizeLimit) ||
      (selected === 1 && entries?.selected.length > exportSizeLimit)
    );
  }

  // RXFE-54a23: only 'all results' is available for dpitems and tgitems context
  const fullRangeCheck = ranges && ranges.length > 1;

  if (type === 'empty' && fullRangeCheck) {
    return selected === 1 && ranges[1]?.value === 0;
  }

  if (type === 'fromto' && fullRangeCheck) {
    return (selected === 2 && (!from || !to)) || from <= 0 || to <= 0 || to > ranges[2]?.max || from > to;
  }

  return false;
};

export const validateForm = () => (dispatch, getState) => {
  const isResultsExport = isResultsExportSelector(getState());
  const isChemQuoteExport = isChemQuoteExportSelector(getState());

  // Specific checks only for Export in result page
  if (isResultsExport || isChemQuoteExport) {
    const warningRangeEmpty = dispatch(getWarningRangeVisible('empty'));
    const warningRangeFromToError = dispatch(getWarningRangeVisible('fromto'));
    const warningRangeHigh = dispatch(getWarningRangeVisible('high'));
    const formError = warningRangeEmpty || warningRangeFromToError || warningRangeHigh;

    dispatch(setErrors(EXPORT_ERRORS.RANGE_EMPTY, warningRangeEmpty));
    dispatch(setErrors(EXPORT_ERRORS.RANGE_FROM_TO, warningRangeFromToError));
    dispatch(setErrors(EXPORT_ERRORS.RANGE_HIGH, warningRangeHigh));
    dispatch(setErrors(EXPORT_ERRORS.FORM_ERROR, formError));
  }

  const errors = errorsSelector(getState());
  return errors[EXPORT_ERRORS.FORM_ERROR];
};

export const setRangeOptions = (name, value) => (dispatch, getState) => {
  const formData = formDataSelector(getState());

  const numVal = parseInt(value);

  formData.range[name] = isNaN(numVal) ? value : numVal;
  dispatch(setFormData(formData));
  dispatch(validateForm());
};

export const setFormDataOptionsHeadline = (value) => (dispatch, getState) => {
  const formData = formDataSelector(getState());
  formData.options.headline = value;
  dispatch(setFormData(formData));
};

export const setExportOptions = (index) => (dispatch, getState) => {
  const data = dataSelector(getState());

  each(data.options, (item, itemIndex) => {
    // eslint-disable-next-line no-param-reassign
    item.checked = itemIndex === index ? !item.checked : item.checked;
  });

  dispatch(setData(data));
};

export const setRange = (index) => (dispatch, getState) => {
  const formData = formDataSelector(getState());
  const data = dataSelector(getState());

  formData.range.selected = index;
  const dataRange = get(data, `ranges[${index}]`);

  if (dataRange) {
    formData.range.placeholder = `${dataRange.label}${dataRange.value}`;
  }

  dispatch(setFormData(formData));

  dispatch(validateForm());
};

export const initRanges =
  () =>
  // eslint-disable-next-line consistent-return
  (dispatch, getState) => {
    const data = dataSelector(getState());

    if (data.ranges && data.ranges.length > 1) {
      const defaultIndex = data.ranges[1].value > 0 ? 1 : 0;
      return dispatch(setRange(defaultIndex));
    }
    dispatch(setRange(0));
  };

export const setExport = (index) => (dispatch, getState) => {
  const data = dataSelector(getState());
  const formData = formDataSelector(getState());

  // Reset other format options checked prop
  each(data.exports, (item) => {
    // eslint-disable-next-line no-param-reassign
    item.checked = false;
  });

  // Update the checked export
  if (data.exports?.[index]) {
    data.exports[index].checked = true;
    formData.export = data.exports[index].name;
    dispatch(setFormData(formData));
  }

  dispatch(setData(data));
};

const _handleExportFormatChange = (index, formatsBackup) => (dispatch, getState) => {
  const data = dataSelector(getState());

  // Update also the subproperties options
  data.exports = formatsBackup[index]?.export_options;
  data.options = formatsBackup[index]?.additional_options;

  dispatch(setData(data));
  dispatch(setExport(0));

  const formData = formDataSelector(getState());

  // Update format placeholder and value
  formData.format.placeholder = data?.formats[index]?.label;
  formData.format.value = data?.formats[index]?.value;

  dispatch(setFormData(formData));
};

export const setFormat = (index) => (dispatch, getState) => {
  let formatsBackup = formatsBackupSelector(getState());

  if (!formatsBackup) {
    const data = dataSelector(getState());
    data.addHeadlineOptions();
    data.addIncludeOnlyFirstPatentOption();
    formatsBackup = [...data.formats];

    dispatch(setFormatsBackup(formatsBackup));
    dispatch(_handleExportFormatChange(index, formatsBackup));
  }

  const data = dataSelector(getState());

  if (data?.formats[index]?.disabled) {
    return;
  }

  dispatch(setErrors(EXPORT_ERRORS.FORMAT, false));

  // Reset other format options checked prop
  each(data.formats, (format, i) => {
    // eslint-disable-next-line no-param-reassign
    format.checked = index === i;
  });

  dispatch(setData(data));
  dispatch(_handleExportFormatChange(index, formatsBackup));
};

export const _storeExportDialogStuff = (formData, data, exportType, selectedDatapointsIds) => {
  const storeObj = {
    format: formData?.format?.value,
    range: pick(formData?.range, ['selected', 'from', 'to']),
    export: formData?.export,
    selectedDatapointsIds,
    options: {
      checked: _.chain(data.options)
        .filter((opt) => opt.checked)
        .map((opt) => opt.name)
        .value(),
      headline: formData?.options?.headline,
    },
  };

  ExportReopenService.storeExportDialogStuff(storeObj);
  ExportReopenService.saveReopenExport(exportType);
};

export const signIn = (context) => (dispatch, getState) => {
  const formData = formDataSelector(getState());
  const data = dataSelector(getState());
  const exportType = exportTypeSelector(getState());
  const selectedDatapoints = selectedDatapointsSelector(getState());

  Catalyst.trackConversionDriverClick(`${Catalyst.mapContext(context)}:export:sign-in`);
  dispatch(closeExportModal());
  AppStorage.instance.set('revisitLastState', true);
  _storeExportDialogStuff(formData, data, exportType, selectedDatapoints.ids);
  history.push({
    pathname: generatePath(paths.LOGIN),
  });
};

// eslint-disable-next-line consistent-return
export const resolveFormError = (errors, data) => {
  if (errors[EXPORT_ERRORS.RANGE_EMPTY]) {
    return 'This range selected cannot be zero.';
  }

  if (errors[EXPORT_ERRORS.RANGE_HIGH]) {
    return errors[EXPORT_ERRORS.RANGE_HIGH_BIOACTIVITY_VISUALIZATION]
      ? `The selected export range exceeds the limit of ${data.exportSizeLimit} substance and target results.`
      : `The selected export range exceeds the limit of ${data.exportSizeLimit}.`;
  }

  if (errors[EXPORT_ERRORS.RANGE_FROM_TO]) {
    return 'This range is invalid.';
  }
};

export const trackAdobeAnalyticsError = () => (dispatch, getState) => {
  const errors = errorsSelector(getState());
  const data = dataSelector(getState());
  const error = resolveFormError(errors, data);

  if (!error) {
    return;
  }

  Catalyst.trackError(
    {
      message: error,
      component: 'Export',
    },
    'fe-'
  );
};

export const _createExportRequestPayload = (formData, currentState, parentParams, isChemQuoteExport) => {
  if (isChemQuoteExport) {
    return ExportService.prepareChemQuoteExportPayload(formData);
  }

  const data = dataSelector(currentState);
  const entries = entriesSelector(currentState);
  const payload = ExportService.prepareResultsExportPayload(formData);

  // extend payload with options
  if (data && data.options) {
    data.options.forEach((option) => {
      if (!option.dummy && !option.hidden && option.checked) {
        payload.options.push(`${[option.name]}`);
      }
    });
  }

  // Update range if "Include only first patent of a family" is selected
  if (payload?.options?.includes(ExportService.includeOnlyFirstPatentOptionName) && formData?.range?.selected === 0) {
    payload.range = `1-${entries?.totalResult}`;
  }

  const { databaseId, context } = parentParams;
  const user = Auth.getUser();
  const databaseName = get(user, `details.databases[${databaseId}].name`);

  const isRcsExport = databaseName === 'Commercial Substances';
  const isProductSelectionEnabled = Auth.isFeatureEnabled(FEATURES.enableProductSelection);

  if (isRcsExport && formData?.range?.selected === 2 && isProductSelectionEnabled) {
    const normalizedCheckedItems = normalizedCheckedItemsSelector(currentState);

    const selectedProducts = normalizedCheckedItems
      .filter(({ products }) => !isEmpty(products))
      .reduce((memo, { products }) => memo.concat(products.map(({ productId }) => productId)), []);

    const selectedRcsSubstances = normalizedCheckedItems.map(({ id }) => id);
    payload.rcssupplierIds = selectedProducts;
    payload.rcsSubstanceIds = selectedRcsSubstances;
  }

  // Patent Family Clustering
  if (context === citations) {
    payload.options.push('flattenGroup');
  }

  return payload;
};

export const _createExportRequest = (
  formData,
  currentState,
  parentParams,
  exportType,
  similarityMol,
  semanticSearchIncluded,
  semanticResultName,
  textQuery
) => {
  const isChemQuoteExport = exportType === EXPORT_TYPES.CHEMQUOTE;

  let payload = _createExportRequestPayload(formData, currentState, parentParams, isChemQuoteExport);
  if (similarityMol) {
    payload = { ...payload, similarityMol };
  }

  // Add semantic search parameters to payload
  if (semanticSearchIncluded !== undefined) {
    payload = { ...payload, semanticSearchIncluded };
  }
  if (semanticResultName !== undefined) {
    payload = { ...payload, semanticResultName };
  }
  if (textQuery !== undefined) {
    payload = { ...payload, textQuery };
  }

  // AA tracking - prepare content object
  const omittedRange = omit(formData.range, 'placeholder');
  const isNewExcelLayout = formData?.format?.placeholder === ExportService.microsoftExcelNewLayout;

  const trackingContent = {
    detail: `range: ${JSON.stringify(omittedRange)}; options: ${JSON.stringify(formData.options)}`,
    exportType: isChemQuoteExport ? 'Reaxys-Export-Chemquote' : formData.export || formData.tab,
    format: isNewExcelLayout ? 'msexcel new' : formData.format.value,
    id: `export:${uuid()}`,
    type: isChemQuoteExport ? 'Reaxys-Export-Chemquote' : 'REAXYS-EXPORT:SCOPE-ABSTRACT',
  };

  const request = ExportRequestManager.sendExportRequest(exportType, payload, trackingContent);
  return { request, trackingContent };
};

export const _trackExportApply = (trackingContent) => (dispatch, getState) => {
  const formData = formDataSelector(getState());
  const data = dataSelector(getState());
  const selectedDatapoints = selectedDatapointsSelector(getState());
  const isResultsExport = isResultsExportSelector(getState());
  const isChemQuoteExport = isChemQuoteExportSelector(getState());

  if (!isResultsExport && !isChemQuoteExport) {
    Catalyst.trackExport(trackingContent, undefined, selectedDatapoints.categories);
    return;
  }

  // AA tracking - prepare additionally, rows and datapoints
  let rows;
  const {
    range: { from, to, selected },
  } = formData;

  const selectedData = data?.ranges[selected];
  const rangeDiff = to - from + 1;

  if (isNaN(rangeDiff)) {
    // If no range - pass selected value
    rows = selectedData.value;
  }

  if (!isNaN(rangeDiff) && rangeDiff > 0 && from >= 0) {
    // If correct range is set
    rows = rangeDiff;
  }

  // AA tracking - track
  Catalyst.trackExport(trackingContent, rows, selectedDatapoints.categories);
};

export const apply = (parentParams) => (dispatch, getState) => {
  // In case of errors, stop execution
  const isNotValidForm = dispatch(validateForm());
  if (isNotValidForm) {
    return;
  }

  const isSelectionExportActive = isSelectionExportActiveSelector(getState());
  const selectedDatapoints = selectedDatapointsSelector(getState());
  const formData = formDataSelector(getState());
  const exportType = exportTypeSelector(getState());
  const similarityMol = similarityMolSelector(getState());

  const updatedFormData = { ...formData };

  if (isSelectionExportActive) {
    updatedFormData.items = selectedDatapoints.ids;
  }

  const entries = entriesSelector(getState());
  if (entries && entries.total) {
    updatedFormData.resultSize = entries.total;
  }

  dispatch(setFormData(updatedFormData));

  // Send request
  const { semanticSearchIncluded, semanticResultName, textQuery } = parentParams;
  const { request, trackingContent } = _createExportRequest(
    updatedFormData,
    getState(),
    parentParams,
    exportType,
    similarityMol,
    semanticSearchIncluded,
    semanticResultName,
    textQuery
  );
  exportRequest = request;
  dispatch(_trackExportApply(trackingContent));
  dispatch(closeExportModal());

  // eslint-disable-next-line consistent-return
  return exportRequest;
};

export const cancel = () => exportRequest && exportRequest.cancel();

export const showExportBusyInfoDialog = () => (dispatch, getState) =>
  NotifyDialogsService.openModalWindow({
    title: EXPORT_TEXTS.busyInfoDialogTitle,
    text: EXPORT_TEXTS.busyInfoDialogText,
    confirmText: EXPORT_TEXTS.busyInfoDialogButton,
    type: 'info',
    backdrop: 'static',
    onConfirm: () => {
      const isBusy = isBusySelector(getState());
      if (!isBusy) {
        return;
      }
      dispatch(closeExportModal());
    },
  });

export const _checkAndSetStoredExportDialogStuff = () => (dispatch, getState) => {
  const data = dataSelector(getState());
  const formData = formDataSelector(getState());

  const stored = ExportReopenService.getStoredExportDialogStuff();

  if (!stored) {
    return;
  }

  // Format
  if (stored.format) {
    const index = findIndex(data.formats, (format) => format.value === stored.format);
    if (index !== -1) {
      dispatch(setFormat(index));
    }
  }

  // Range
  if (stored.range) {
    dispatch(setRange(stored.range.selected));
    extend(formData.range, pick(stored.range, ['from', 'to']));
    dispatch(setFormData(formData));
  }

  // Export
  if (stored.export) {
    const index = findIndex(data.exports, (ex) => ex.name === stored.export);
    if (index !== -1) {
      dispatch(setExport(index));
    }
  }

  // Selected datapoints ids
  if (stored.selectedDatapointsIds) {
    const selectedDatapoints = selectedDatapointsSelector(getState());
    dispatch(setSelectedDatapoints({ ...selectedDatapoints, ids: stored.selectedDatapointsIds }));
  }

  // Options
  if (stored.options) {
    each(data.options, (opt) => {
      // eslint-disable-next-line no-param-reassign
      opt.checked = includes(stored.options.checked, opt.name);
    });

    dispatch(setData(data));

    formData.options.headline = stored.options.headline;
    dispatch(setFormData(formData));
  }

  // Delete from storage
  ExportReopenService.storeExportDialogStuff(null);
};

export const initialize =
  ({ exportType, entries, parentParams }, history) =>
  (dispatch, getState) => {
    dispatch(resetState());
    // Ensure FORMAT error is always cleared during initialization to prevent stuck disabled state
    dispatch(setErrors(EXPORT_ERRORS.FORMAT, false));

    if (history) {
      locationChangeSubscription = history.listen(() => {
        dispatch(closeExportModal());
      });
    }
    Catalyst.trackExport(null);

    dispatch(setEntries(entries));

    const warningRangeHighAnonymousHint = Auth.isAnonymousUser() === true || Auth.isAnonShibbolethLogin();
    dispatch(setErrors(EXPORT_ERRORS.RANGE_HIGH_ANONYMOUS_HINT, warningRangeHighAnonymousHint));
    dispatch(setErrors(EXPORT_ERRORS.RANGE_HIGH_BIOACTIVITY_VISUALIZATION, EXPORT_TEXTS.warningRangeHighBioactivityVisualizationTooltip));

    dispatch(setExportType(exportType));

    // Populate data container
    const data = ExportService.initializeData({
      exportType,
      parentParams,
      entries,
    });

    dispatch(setData(data));
    dispatch(initRanges());

    const isResultsExport = isResultsExportSelector(getState());
    const isChemQuoteExport = isChemQuoteExportSelector(getState());

    if (isResultsExport) {
      dispatch(setCurrentView(EXPORT_VIEWS.MAIN));
      const value = ExportService.isFormatsExist();
      if (value) {
        dispatch(setFormat(0));
        dispatch(_checkAndSetStoredExportDialogStuff());
      } else {
        // If no formats exist, ensure FORMAT error is cleared to prevent button from being stuck disabled
        dispatch(setErrors(EXPORT_ERRORS.FORMAT, false));
      }
    } else if (isChemQuoteExport) {
      dispatch(setErrors(EXPORT_ERRORS.FORMAT, false));
      dispatch(_checkAndSetStoredExportDialogStuff());
    }

    const isBusy = isBusySelector(getState());

    if (isBusy) {
      dispatch(showExportBusyInfoDialog());
    }
  };

export const changeDatapoints =
  ({ selectedTaxonomies: selected, selectedRootTaxonomies: selectedCategories }) =>
  (dispatch, getState) => {
    const selectedDatapoints = selectedDatapointsSelector(getState());

    dispatch(
      setSelectedDatapoints({
        ...selectedDatapoints,
        count: selected.length,
        items: selected,
        categories: selectedCategories,
      })
    );
  };

export const getDatapoints = () => (dispatch) => {
  if (datapoints) {
    return Promise.resolve(datapoints);
  }

  dispatch(setIsLoading(true));

  return ExportService.getDatapoints()
    .then((respDatapoints) => {
      // Sort alphabetically
      datapoints = sortBy(respDatapoints, (datapoint) => datapoint.name);
      eachHierarchy(datapoints, (datapoint) => {
        if (datapoint.children) {
          // eslint-disable-next-line no-param-reassign
          datapoint.children = sortBy(datapoint.children, (child) => child.name);
        }
      });
      dispatch(setIsLoading(false));
      return datapoints;
    })
    .catch((e) => {
      Logger.reject(e);
      dispatch(setIsLoading(false));
    });
};

export const clearDatapoints = () => {
  datapoints = null;
};

export const collectChildren = (item) => {
  let collected = [];

  each(item.children, (child) => {
    if (child.validForExportById) {
      collected.push(child.id);
    } else {
      collected = collected.concat(collectChildren(child));
    }
  });

  return collected;
};

export const getItemsPayload = (selectedDatapoints) => {
  let items = [];

  each(selectedDatapoints, (item) => {
    if (item.validForExportById) {
      items.push(item.id);
    } else {
      items = items.concat(collectChildren(item));
    }
  });

  return items;
};

export const applyDatapoints = () => (dispatch, getState) => {
  const selectedDatapoints = selectedDatapointsSelector(getState());
  const ids = getItemsPayload(selectedDatapoints.items);

  dispatch(setSelectedDatapoints({ ...selectedDatapoints, ids }));
  dispatch(setCurrentView(EXPORT_VIEWS.MAIN));
};

// Show "Include only first patent of a family" option
export function showIncludeOnlyFirstPatentOption() {
  return (dispatch, getState) => {
    const data = dataSelector(getState());
    data.options?.forEach((option) => {
      if (option.name === ExportService.includeOnlyFirstPatentOptionName) {
        // eslint-disable-next-line no-param-reassign
        option.hidden = false;
      }
    });

    dispatch(setData(data));
  };
}

// Hide "Include only first patent of a family" option
export function hideIncludeOnlyFirstPatentOption() {
  return (dispatch, getState) => {
    const data = dataSelector(getState());

    data.options?.forEach((option) => {
      if (option.name === ExportService.includeOnlyFirstPatentOptionName) {
        // eslint-disable-next-line no-param-reassign
        option.hidden = true;
      }
    });

    dispatch(setData(data));
  };
}
